import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import { Heading } from '@chakra-ui/react';

// Utility function to get the ordinal of a number
function getOrdinal(n) {
    if (n === 0) return "Feature time series";
    const suffixes = { 1: 'st', 2: 'nd', 3: 'rd' };
    const suffix = suffixes[n] || 'th';
    return `${n}${suffix} order time derivative`;
}

function firstDerivative(arr) {
    let derivative = [];
    for (let i = 0; i < arr.length - 1; i++) {
        derivative.push(arr[i + 1] - arr[i]);
    }
    derivative.push(0);
    return derivative;
}

function nthDerivative(arr, n) {
    if (n < 1) return arr.slice();
    let result = arr.slice();
    for (let i = 0; i < n; i++) {
        result = firstDerivative(result);
    }
    return result;
}

const LinePlot = ({
    title = '',
    data = [1],
    width = 500,
    height = 400,
    marginTop = 20,
    marginRight = 20,
    marginBottom = 30,
    marginLeft = 60,
}) => {
    const svgRef = useRef();
    const gxRef = useRef();
    const gyRef = useRef();
    const [dtn, setDtn] = useState(0);

    const newData = nthDerivative(data, dtn);

    const xScale = d3.scaleLinear([0, newData.length - 1], [marginLeft, width - marginRight]);
    const yScale = d3.scaleLinear(d3.extent(newData), [height - marginBottom, marginTop]);

    useEffect(() => {
        const svg = d3.select(svgRef.current);

        // Clear only the path and circles, leaving axes intact
        svg.selectAll('.line-path').remove();
        svg.selectAll('.dot').remove();

        // X and Y Axes
        d3.select(gxRef.current).call(d3.axisBottom(xScale).ticks(8));
        d3.select(gyRef.current).call(d3.axisLeft(yScale).ticks(6));

        // Draw the line plot
        const line = d3.line((d, i) => xScale(i), d => yScale(d));
        svg.append('path')
            .datum(newData)
            .attr('class', 'line-path')
            .attr('fill', 'none')
            .attr('stroke', '#2b6cb0')
            .attr('stroke-width', 1.5)
            .attr('d', line);

        // Draw data points
        svg.selectAll('.dot')
            .data(newData)
            .enter()
            .append('circle')
            .attr('class', 'dot')
            .attr('cx', (d, i) => xScale(i))
            .attr('cy', d => yScale(d))
            .attr('r', 2.5)
            .attr('fill', 'white')
            .attr('stroke', '#2b6cb0')
            .attr('stroke-width', 1.5);
    }, [newData, width, height, marginLeft, marginBottom, marginRight, marginTop, xScale, yScale]);

    return (
        <div className="plot-container">
            {title && (
                <div className="head">
                    <Heading as="h5" size="sm" mb={4}>{`Feature: ${title}`}</Heading>
                    <div style={{ display: "flex", alignItems: "center", marginVertical: 10 }}>
                        <Heading as="h6" size="sm" mr={4}>{`Derivative: `}</Heading>
                        <input
                            type="range"
                            min="0"
                            max="4"
                            value={dtn}
                            onChange={(e) => setDtn(Number(e.target.value))}
                            style={{ marginRight: '10px' }}
                        />
                        <Heading as="h6" size="sm">{`${getOrdinal(dtn)}`}</Heading>
                    </div>
                </div>
            )}
            <svg ref={svgRef} width={width} height={height}>
                <g ref={gxRef} transform={`translate(0, ${height - marginBottom})`} />
                <g ref={gyRef} transform={`translate(${marginLeft}, 0)`} />
            </svg>
        </div>
    );
};

LinePlot.propTypes = {
    title: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.number),
    width: PropTypes.number,
    height: PropTypes.number,
    marginTop: PropTypes.number,
    marginRight: PropTypes.number,
    marginBottom: PropTypes.number,
    marginLeft: PropTypes.number,
};

export default LinePlot;
